import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PAGE } from '../../app.routes';
import { APP_CONFIGURATION_TOKEN, LoaderService, ToastService } from '../../core';
import { RequestFrom } from '../../models';
import { PlanSettingsService, TreatmentService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';
import { NotificationThresholdComponent } from '../notification-threshold';
import { PlanHeaderComponent } from '../plan-header';
import { TreatmentProgressBarComponent } from '../treatment-progress-bar';


/**
 * Component for plan settings page.
 */
@Component({
	selector: 'tsp-plan-settings',
	imports: [
		PlanHeaderComponent,
		TreatmentProgressBarComponent,
		NotificationThresholdComponent
	],
	templateUrl: './plan-settings.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanSettingsComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly treatSrv = inject(TreatmentService);
	private readonly planStgSrv = inject(PlanSettingsService);
	private readonly configs = inject(APP_CONFIGURATION_TOKEN);

	planGuid = input<string>();
	revoke = input<boolean>(false);
	reqFrom = input<RequestFrom>();

	plan = computed(() => this.planStgSrv.plan());

	progress = computed(() => this.planStgSrv.progress());
	consolidationDays = this.planStgSrv.consolidationDays;
	isConsolidationValid = this.planStgSrv.isConsolidationValid;
	threshold = this.planStgSrv.threshold;

	isVisible = computed(() => !!this.planStgSrv.progress());
	isReadonly = computed(() => this.planStgSrv.isReadonly() || this.revoke());
	isUpdateDisabled = computed(() => !this.isConsolidationValid());
	isRevokeVisible = computed(() => !this.planStgSrv.isReadonly());


	ngOnInit(): void {
		if (this.planGuid()) {
			this.planStgSrv.init(this.planGuid()!).subscribe(res => {
				if (!res) this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_LOAD_ERROR);
			});
		} else {
			throw new Error('Plan Guid is required!');
		}
	}

	/**
	 * Back to List of Patients page or Tlhex report (without save).
	 */
	cancel(): void {
		if (this.reqFrom() === RequestFrom.PATIENT) {
			this.loaderSrv.show();
			window.location.href = this.configs.planListSite;
		} else if (this.reqFrom() === RequestFrom.TLHEX) {
			this.loaderSrv.show();
			window.location.href = `${this.configs.tlhexSite}${this.planGuid()}`;
		} else {
			this.router.navigateByUrl(PAGE.PATIENT_LIST);
		}
	}

	/**
	 * Open revoke confirmation modal.
	 */
	openRevokeModal(): void {
		const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_MODAL_TITLE,
			message: this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_MODAL_MSG,
			confirmBtn: this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_MODAL_CONFIRM,
			cancelBtn: this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_MODAL_CANCEL
		};
		modalRef.result.then(() => this._revoke()).catch(() => false);
	}

	/**
	 * Revoke plan.
	 */
	private _revoke(): void {
		this.treatSrv.revoke(this.planGuid()!).pipe(
			catchError(err => {
				this.loaderSrv.hide();
				return throwError(() => err);
			})
		).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_SUCCESS);
				if (this.reqFrom() === RequestFrom.PATIENT || this.reqFrom() === RequestFrom.TLHEX) {
					setTimeout(() => window.location.href = this.configs.planListSite, 3000);
				} else {
					setTimeout(() => this.router.navigateByUrl(PAGE.PATIENT_LIST), 3000);
				}
			},
			error: () => this.toastSrv.showError(this.labels().PLAN_SETTINGS_COMPONENT_REVOKE_ERROR)
		});
	}

	/**
	 * Update plan settings.
	 */
	update(): void {
		if (!this.isUpdateDisabled()) {
			this.planStgSrv.update().subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PLAN_SETTINGS_COMPONENT_UPDATE_SUCCESS);
					if (this.reqFrom() === RequestFrom.PATIENT) {
						setTimeout(() => window.location.href = this.configs.planListSite, 3000);
					} else {
						setTimeout(() => this.router.navigateByUrl(PAGE.PATIENT_LIST), 3000);
					}
				},
				error: () => this.toastSrv.showError(this.labels().PLAN_SETTINGS_COMPONENT_UPDATE_ERROR)
			});
		}
	}


	ngOnDestroy(): void {
		this.planStgSrv.reset();
	}

}
